import { gql } from "@apollo/client";
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Dialog,
  IconButton,
  Paper,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InfoIcon from '@mui/icons-material/Info';

import { datef } from "@caredna-marlin/utils/dates";
import EditPatient from "~/components/patient/EditPatient";

import { ListActivePatientsQuery, ActivePatientOrderBy, ListActivePatientsQueryVariables, OrderDirection, useListActivePatientsQuery, useListActiveHospitalAtHomeEpisodesQuery } from "~/graphql/__generated__/graphql";
import { formatPhoneNumber } from "~/utils/format";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";
import LazyLoadTable, { Column } from "~/components/common/LazyLoadTable";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { isHospitalAtHomeServiceLine } from "~/utils/intakeServiceLine";
import { DischargeReadinessDashboard } from "~/components/intake/DischargeReadinessDashboard";

const cols: Column<keyof ActivePatientOrderBy>[] = [
  { label: "Name", sort: ["lastName", "firstName"] },
  { label: "Next Visit", sort: ["nextVisitStartTime"], sx: { textAlign: "center" } },
  { label: "DOB", sort: ["dateOfBirth"] },
  { label: "City", sort: ["addressCity"] },
  { label: "State", sort: ["addressState"] },
  { label: "Zip", sort: ["addressZip"] },
  { label: "Phone", sort: ["phone"] },
  { label: "", sort: false, sx: { width: "1%" } },
];

const PatientList = () => {
  const [addPatient, setAddPatient] = useState(false);
  const [addedPatient, setAddedPatient] = useState<{ id: number, firstName: string, lastName: string } | undefined>(undefined);
  const [readinessPatient, setReadinessPatient] = useState<ActivePatientRow | undefined>(undefined);

  // Patients on an active Hospital at Home episode get a Discharge Readiness
  // Dashboard action. The program is identified by service line name so it
  // follows however the org has named the line.
  const orgConfig = useOrgConfig();
  const hahServiceLines = useMemo(
    () =>
      Object.values(orgConfig.serviceLines).filter(
        (sl): sl is NonNullable<typeof sl> => !!sl && isHospitalAtHomeServiceLine(sl.name),
      ),
    [orgConfig.serviceLines],
  );
  const hahServiceLineIds = useMemo(() => hahServiceLines.map((sl) => sl.id), [hahServiceLines]);
  const { data: hahEpisodeData } = useListActiveHospitalAtHomeEpisodesQuery({
    variables: { serviceLineIds: hahServiceLineIds },
    skip: hahServiceLineIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });
  const hahPatientIds = useMemo(
    () => new Set((hahEpisodeData?.episode ?? []).map((e) => e.patientId)),
    [hahEpisodeData],
  );
  // Only one Hospital at Home line in practice; fall back to a generic label.
  const hahProgramLabel = hahServiceLines[0]?.name ?? "Hospital at Home";

  const [variables, setVariables] = useState<ListActivePatientsQueryVariables>({
    offset: 0,
    orderBy: {
      lastName: { direction: OrderDirection.Asc, priority: 1 },
      firstName: { direction: OrderDirection.Asc, priority: 2 },
    } as ActivePatientOrderBy
  });

  const { data, loading } = useListActivePatientsQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true, // Update loading state for network requests
  });

  return <Box
    sx={{
      height: "100%",
      width: "100%",
      overflow: "hidden",
      mt: 4,
      mb: 4,
    }}
  >
    <Paper sx={{ width: '100%', p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6">
          Active Patients <Tooltip title={"This list shows active patients. To find former or other patients use the search bar."} placement="right"><InfoIcon fontSize="small" color="primary" /></Tooltip>
        </Typography>
        <PermissionOrHide permission={Permission.managePatients}>
          <Button variant="contained" size="medium" onClick={() => setAddPatient(true)}>Add Patient</Button>
        </PermissionOrHide>
      </Box>
      {addedPatient && <Alert severity="success" onClose={() => setAddedPatient(undefined)} sx={{mt: 1}}>Created patient <Link to={`/patients/${addedPatient?.id}`}>{addedPatient?.firstName} {addedPatient?.lastName}</Link></Alert>}
      <LazyLoadTable
        columns={cols}
        emptyLabel="No Supply Items to display"
        items={data?.activePatients}
        loading={loading}
        queryVariables={variables}
        setQueryVariables={setVariables}
        renderRow={p => (
          <PatientRow
            p={p}
            key={p.id}
            onShowDischargeReadiness={hahPatientIds.has(p.id) ? () => setReadinessPatient(p) : undefined}
          />
        )}
      />
    </Paper>

    {addPatient && <EditPatient
      onClose={(newPatient) => {
        setAddedPatient(newPatient);
        setAddPatient(false);
      }}
    />}

    <Dialog
      open={readinessPatient !== undefined}
      onClose={() => setReadinessPatient(undefined)}
      maxWidth="md"
      fullWidth
    >
      {readinessPatient && (
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setReadinessPatient(undefined)}
            size="small"
            sx={{ position: "absolute", top: 8, right: 8, color: "common.white", zIndex: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <DischargeReadinessDashboard
            patientName={`${readinessPatient.firstName.charAt(0)}. ${readinessPatient.lastName}`}
            programLabel={hahProgramLabel}
          />
        </Box>
      )}
    </Dialog>
  </Box>
}

export default PatientList;

type ActivePatientRow = ListActivePatientsQuery["activePatients"][number];

const PatientRow = ({ p, onShowDischargeReadiness }: { p: ActivePatientRow, onShowDischargeReadiness?: () => void }) => <TableRow>
  <TableCell><Link to={`/patients/${p.id}`}>{p.lastName}, {p.firstName}</Link></TableCell>
  <TableCell sx={{ textAlign: "center" }}>
    <NextVisit visit={p.nextVisit} timezone={p.addressTimezone} />
  </TableCell>
  <TableCell>{p.dateOfBirth ? datef(new Date(p.dateOfBirth!), "MMM d, yyyy", "UTC") : '----' }</TableCell>
  <TableCell>{p.addressCity}</TableCell>
  <TableCell>{p.addressState}</TableCell>
  <TableCell>{p.addressZip}</TableCell>
  <TableCell>{formatPhoneNumber(p.phone)}</TableCell>
  <TableCell sx={{ width: "1%", whiteSpace: "nowrap", py: 0 }}>
    {onShowDischargeReadiness && (
      <Tooltip title="Discharge Readiness Dashboard">
        <IconButton color="primary" onClick={onShowDischargeReadiness}>
          <FactCheckIcon />
        </IconButton>
      </Tooltip>
    )}
  </TableCell>
</TableRow>

const NextVisit = ({ visit, timezone }: { visit?: ListActivePatientsQuery["activePatients"][number]["nextVisit"], timezone?: ListActivePatientsQuery["activePatients"][number]["addressTimezone"]}) => {
  const theme = useTheme();

  if (!visit) { return "-" }

  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const showTz = timezone && timezone != userTz ? timezone : undefined;

  return <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ alignItems: "center", justifyContent: "center" }}>
    {datef(new Date(visit.startTime), `MMM d, h:mm aa${showTz ? " zzz":""}`, showTz)}
    <Tooltip title={visit.providers.map(p => `${p?.firstName} ${p?.lastName}`).join(', ')}>
      <AvatarGroup spacing={20}>
        {visit.providers.map((provider, idx) => (
        <Avatar key={idx} sx={{ width: 24, height: 24, fontSize: "0.8rem", bgcolor: theme.palette.primary.main }}>
          {provider?.firstName?.[0]}{provider?.lastName?.[0]}
        </Avatar>
      ))}        
      </AvatarGroup>
    </Tooltip>
  </Stack>
}

gql(`
  query ListActiveHospitalAtHomeEpisodes($serviceLineIds: [Int!]!)
  @permission(requires: useWebApp)
  {
    episode(where: {
      active: { eq: true }
      deleted: { eq: false }
      serviceLineId: { inArray: $serviceLineIds }
    }) {
      id
      patientId
    }
  }
`);

gql(`
  query ListActivePatients($offset: Int!, $orderBy: ActivePatientOrderBy!)
  @permission(requires: useWebApp)
  {
    activePatients(
      offset: $offset
      limit: 50
      orderBy: $orderBy
    ) {
      id
      firstName
      lastName
      addressCity
      addressState
      addressZip
      addressTimezone
      dateOfBirth
      phone
      nextVisit {
        startTime
        providers {
          firstName
          lastName
        }
      }
    }
  }
`);
